
  import ApprovalsForbidden from '@/ui/pages/approvals/ApprovalsForbidden.vue';
  import { defineComponent, ref, watch } from 'vue';
  import { d, t } from '@/ui/i18n/i18n';
  import { Tag, tag } from '@/ui/components';
  import {
    bulkDelete,
    filterStatus as getAllStatuses,
    filterType as getAllTypes,
    list as getApprovalList,
    remove,
  } from '@/services/api/resources/approval';
  import { filter as getAllUsers } from '@/services/api/resources/user';
  import { emptyList } from '@/services/api/response/types';
  import { usePagination, useSorting, useTask } from '@/ui/concerns';
  import * as qs from '@/ui/queryState';
  import { updateMultiple } from '@/ui/queryState';
  import { filterListToOptions, sequence } from '@/ui/helpers/uiHelper';
  import { SelectOptions, SortDirection } from '@/ui/types';
  import { useBulkDeletable } from '@/ui/pages/useBulkDeletable';
  import { useApproval } from '@/ui/concerns/approval/useApproval';
  import { ApprovalStatus, ApprovalType } from '@/entities/approvals/constants';
  import { ApprovalList, ApprovalRecord } from '@/entities/approvals/model';
  import { useApprovalCheck } from '@/ui/pages/approvals/useApprovalsCheck';
  import { BulkDeletableTable } from '@/ui/components/table/BulkDeletableTable';
  import { Box } from '@/ui/components/Box';
  import ApprovalTypeComponent from '@/ui/components/approval/ApprovalType.vue';
  import ApprovalEmpty from '@/ui/pages/approvals/ApprovalEmpty.vue';
  import { approvalStatusToText, approvalTypeToText } from '@/ui/dictionary';
  import { Loader } from '@/ui/components/Loader';
  import { PageTitle } from '@/ui/components/PageTitle';
  import Level from '@/ui/components/common/Level.vue';
  import { Pagination } from '@/ui/components/Pagination';
  import ApprovalFilters from '@/ui/components/approval/ApprovalFilters.vue';
  import { dateRangeInputCallback } from '@/ui/components/filters/dateFilter';
  import { approvalsPermissions, hasSomePermission, Permissions, approvalsEnabledForOrganization } from '@/ui/auth/auth';
  import { renderForbiddenPage } from '@/ui/pages/error/ForbiddenPage';
  import { router } from '@/ui/routes';

  export default defineComponent({
    name: 'ApprovalIndexPage',
    components: {
      ApprovalsForbidden,
      Pagination,
      Level,
      PageTitle,
      Loader,
      ApprovalEmpty,
      ApprovalTypeComponent,
      Box,
      BulkDeletableTable,
      Tag,
      ApprovalFilters,
    },
    setup(_, ctx) {
      if (!hasSomePermission(approvalsPermissions)) {
        return renderForbiddenPage;
      }

      const initialLoading = ref(true);

      const { page, perPage, resetPagination } = usePagination();
      const { checkResult, performCheck } = useApprovalCheck();

      const { sort, dir, onSortChange } = useSorting('updatedAt', SortDirection.Descending);
      const search = qs.string('q', '');
      const doneBy = qs.numbers('doneBy', []);
      const approvers = qs.numbers('approvers', []);
      const status = qs.numbers('status', []);
      const type = qs.numbers('type', []);
      const start = qs.nullableDate('updatedAtStart', null);
      const end = qs.nullableDate('updatedAtEnd', null);

      // get filters task
      const users = ref<SelectOptions>([]);
      const statuses = ref<SelectOptions>([]);
      const types = ref<SelectOptions>([]);
      const getFilters = useTask(
        () => sequence(getAllTypes(), getAllStatuses(), getAllUsers()),
        ([typesList, statusList, userList]) => {
          types.value = filterListToOptions(typesList).map(({ value, label }) => ({
            value,
            label: approvalTypeToText(label as ApprovalType),
          }));
          statuses.value = filterListToOptions(statusList).map(({ value, label }) => ({
            value,
            label: approvalStatusToText(label as ApprovalStatus),
          }));
          users.value = filterListToOptions(userList);
        },
      );

      const sortRows = (field: string, direction: SortDirection) => {
        onSortChange(field, direction);
        resetPagination();
      };

      const query = () => ({
        page: page.value,
        perPage: perPage.value,
        sort: sort.value,
        dir: dir.value,
        search: search.value,
        doneBy: doneBy.value,
        approvers: approvers.value,
        status: status.value,
        type: type.value,
        updatedAtStart: start.value,
        updatedAtEnd: end.value,
      });
      // get list task
      const list = ref<ApprovalList>(emptyList());
      const getList = useTask(
        () => getApprovalList(query()),
        listResponse => {
          list.value = listResponse;
        },
      );
      watch([
        page,
        perPage,
        search,
        doneBy,
        approvers,
        start,
        end,
        status,
        type,
        sort,
        dir,
      ], getList.run);

      watch(() => getList.state.value.pending, newVal => {
        initialLoading.value = newVal;
      });

      const { approvalStatusToTagType } = useApproval();

      const approvalStatusTag = (s: ApprovalStatus) => tag(
        approvalStatusToTagType[s],
        approvalStatusToText(s),
      );

      const {
        selectedResources,
        deleteBulkTask,
        onCheckedRowsUpdate,
      } = useBulkDeletable<ApprovalRecord>({
        getList,
        bulkDeleteFn: bulkDelete,
        mapIds: record => record.id,
      });

      const onClick = (ev: any) => {
        if (ev.data.type === ApprovalType.Inspection) {
          router.push(`/inspections/${ev.data.inspection.id}`);
        }

        if (ev.data.type === ApprovalType.Issue) {
          router.push(`/issues/${ev.data.issue.id}`);
        }
      };

      const updateFilter = (name: string, value: any) => updateMultiple(() => {
        resetPagination();

        // eslint-disable-next-line default-case
        switch (name) {
          case 'search':
            search.value = value;
            break;
          case 'doneBy':
            doneBy.value = value;
            break;
          case 'approvers':
            approvers.value = value;
            break;
          case 'status':
            status.value = value;
            break;
          case 'type':
            type.value = value;
            break;
          case 'updatedAt':
            dateRangeInputCallback(start, end, resetPagination)(value);
            break;
        }
      }, true);

      const displayApprovers = (approval: ApprovalRecord) =>
        approval.data.approvers?.map(approver => approver.data.name).join(', ');

      return {
        t,
        d,
        page,
        perPage,
        sort,
        dir,
        search,
        doneBy,
        approvers,
        status,
        type,
        start,
        end,
        statuses,
        types,
        users,
        getFilters,
        query,
        remove,
        list,
        getList,
        initialLoading,
        performCheck,
        checkResult,
        onSortChange,
        resetPagination,
        approvalStatusToTagType,
        approvalStatusTag,
        selectedResources,
        deleteBulkTask,
        onCheckedRowsUpdate,
        sortRows,
        displayApprovers,
        ApprovalType,
        updateFilter,
        approvalStatusToText,
        onClick,
        approvalsEnabledForOrganization,
      };
    },
  });
